import React from 'react';
import Drawer from '@mui/material/Drawer';
import Hidden from '@mui/material/Hidden';
import clsx from 'clsx';
import AppScrollbar from '../../AppScrollbar';
import VerticalNav from '../components/VerticalNav';
import MainSidebar from '../components/MainSidebar';
import { useLayoutContext } from '@crema/context/LayoutContextProvider';
import { RouterConfigData } from '@crema/models/Apps';
import { Fonts } from '@crema/constants/AppEnums';
import AppLogoHorizontal from '../components/AppLogoHorizontal';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { AiOutlinePlus } from 'react-icons/ai';
import NewMappingDialog from '@crema/modules/provisionify/users/NewMappingDialog';
import { postDataApi } from '@crema/hooks/APIHooks';
import { useInfoViewActionsContext } from '@crema/context/InfoViewContextProvider';
import { useNavigate } from 'react-router-dom';
import jwtAxios from '@crema/services/auth/JWT';
import { MappingType } from '@crema/models/provisionify/Enterprise';
import { Snackbar } from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { checkRolesForCreateMapping } from '@crema/helpers';
import { useAuthUser } from '@crema/hooks/AuthHooks';

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

interface ToastType {
  open: boolean;
  type: 'error' | 'success';
  msg: string;
}

type AppSidebarProps = {
  position?: 'left' | 'top' | 'right' | 'bottom';
  variant?: string;
  routesConfig: RouterConfigData[];
  isNavCollapsed: boolean;
  toggleNavCollapsed: () => void;
};

const AppSidebar: React.FC<AppSidebarProps> = ({
  variant = '',
  position = 'left',
  toggleNavCollapsed,
  isNavCollapsed,
  routesConfig,
}) => {
  const { user } = useAuthUser();
  const { footer, footerType } = useLayoutContext();

  const [mappingDialogOpen, setMappingDialogOpen] = React.useState(false);
  const [toast, setToast] = React.useState<ToastType>({
    open: false,
    type: 'error',
    msg: '',
  });

  const navigate = useNavigate();

  const handleToastClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setToast((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleMappingDialogClickOpen = () => {
    setMappingDialogOpen(true);
  };

  const handleMappingDialogClose = () => {
    setMappingDialogOpen(false);
  };

  const createMapping = (
    status: boolean,
    data: MappingType | string,
    enterpriseId: string = ''
  ) => {
    if (status && enterpriseId) {
      setMappingDialogOpen(false);
      setToast({
        open: true,
        type: 'success',
        msg: 'Successfully created a mapping.',
      });
      navigate(`/mappings/${enterpriseId}`);
    } else {
      setToast({
        open: true,
        type: 'error',
        msg: data as string,
      });
    }
  };

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor={position}
          open={isNavCollapsed}
          onClose={() => toggleNavCollapsed()}
          classes={{
            root: clsx(variant),
            paper: clsx(variant),
          }}
          style={{ position: 'absolute' }}
        >
          <MainSidebar>
            <AppLogoHorizontal />

            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                width: '100%',
                fontSize: 16,
                textTransform: 'capitalize',
                padding: '4px 16px 8px',
              }}
            >
              Sign in
            </Button>

            <AppScrollbar
              sx={{
                py: 2,
                height: 'calc(100vh - 70px) !important',
                borderTop: (theme: { palette: { divider: string } }) =>
                  `solid 1px ${theme.palette.divider}`,
                mt: 0.5,
              }}
            >
              <VerticalNav routesConfig={routesConfig} />
            </AppScrollbar>
          </MainSidebar>
        </Drawer>
      </Hidden>
      <Hidden lgDown>
        <MainSidebar>
          <Box sx={{ paddingTop: 6 }}>
            <AppLogoHorizontal />
          </Box>

          <Box sx={{ padding: '30px 40px' }}>
            {!user.roles.includes('SUPER_ADMIN') &&
              checkRolesForCreateMapping(user.roles) && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<AiOutlinePlus />}
                    type="submit"
                    sx={{
                      width: '100%',
                      fontWeight: Fonts.REGULAR,
                      fontSize: 16,
                      padding: '4px 16px 8px',
                    }}
                    onClick={handleMappingDialogClickOpen}
                  >
                    New mapping
                  </Button>

                  <NewMappingDialog
                    open={mappingDialogOpen}
                    handleClose={handleMappingDialogClose}
                    handleSubmit={createMapping}
                  />
                </>
              )}

            <Snackbar
              open={toast.open}
              autoHideDuration={5000}
              onClose={handleToastClose}
            >
              <Alert
                onClose={handleToastClose}
                severity={toast.type}
                sx={{ width: '100%', alignItems: 'center' }}
              >
                {toast.msg}
              </Alert>
            </Snackbar>
          </Box>

          <AppScrollbar
            className={clsx({
              'has-footer-fixed': footer && footerType === 'fixed',
            })}
            sx={{
              py: 2,
              height: 'calc(100vh - 70px) !important',
              mt: 0.5,
              '&.has-footer-fixed': {
                height: {
                  xs: 'calc(100vh - 117px) !important',
                  xl: 'calc(100vh - 127px) !important',
                },
              },
            }}
          >
            <VerticalNav routesConfig={routesConfig} />
          </AppScrollbar>
        </MainSidebar>
      </Hidden>
    </>
  );
};

export default AppSidebar;
